import { useTranslation } from 'react-i18next';
import { useFeatureEnabled } from '../../stores/brandingStore.js';
import { useOnSiteNowCount } from '../../hooks/useNavCounts.js';

/**
 * THE LIVE FIGURE ON THE OPERATIONS ROW.
 *
 * The rail's one piece of information that is not a destination: how many
 * people are checked in at a worksite this minute, so an admin learns the state
 * of the floor without opening a screen.
 *
 * IT IS THE SAME NUMBER AS THE OPERATIONS PAGE'S "On site now" TILE, and it is
 * the same number structurally rather than by agreement — one query key, one
 * request, one `sumOnSiteNow` (see hooks/useNavCounts.ts). Rule 3.2 says the
 * roster has one scale; the rail is the harder of the two places to notice a
 * disagreement, which is exactly why it may not have its own arithmetic.
 *
 * ---------------------------------------------------------------------------
 * WHAT AN UNKNOWN COUNT LOOKS LIKE, AND WHY THE DOT IS PART OF THE ANSWER.
 *
 * `null` is the feed not having answered — first paint, a failed request, a
 * manager whose scope the server declined. It renders as an EM-DASH, which is
 * the same mark the device-approvals page uses for a count it does not have and
 * the same mark the Operations KPI tiles use, so an unknown reads identically
 * wherever the reader meets one. Never a `0`: "nobody is on site" is a fact
 * somebody would act on, and it is the one thing we do not know.
 *
 * AND THE DOT DOES NOT APPEAR AT ALL WHEN THE COUNT IS UNKNOWN. §1 allows this
 * app exactly one moving thing — the live dot at `--duration-pulse`, 1.9s — and
 * motion is a promise that something is current. A dot pulsing beside a dash
 * would be animating the claim we just declined to make. The dash stands alone;
 * the dot and its pulse arrive together with a real number.
 *
 * `motion-safe:` gates the animation, not the dot: a reduced-motion reader
 * still gets the green dot and the figure, which is where the meaning is. The
 * colour never carries it alone — the sr-only sentence names what the number
 * counts, because "329" inside a link called "Operations" is not a number of
 * anything.
 *
 * THE DIGITS ARE INK, NOT GREEN. The handoff sets them at #7ee0b0; on this rail
 * a green that light is a 3.4:1 figure, which clears the non-text floor a DOT
 * answers to and fails the 4.5:1 a NUMBER answers to. So the dot carries the
 * hue and the digits carry the legibility.
 */
export function NavLiveCount(): JSX.Element | null {
  const { t } = useTranslation();
  // Per-WORKSITE occupancy. An office-only tenant has no such feed, and a rail
  // that showed it a dash for ever would be reporting a permanent outage of
  // something it never runs.
  const enabled = useFeatureEnabled('workers');
  const count = useOnSiteNowCount({ enabled });
  if (!enabled) return null;

  const known = count !== null;
  return (
    <span className="ms-auto inline-flex shrink-0 items-center gap-control">
      {known && (
        <span
          aria-hidden
          className="h-1.5 w-1.5 shrink-0 rounded-full bg-success motion-safe:animate-pulse"
          style={{ animationDuration: 'var(--duration-pulse)' }}
        />
      )}
      <span
        aria-hidden
        className={
          known
            ? 'text-copy-sm font-semibold tabular-nums text-nav-on-surface'
            : 'text-copy-sm font-semibold text-nav-on-surface/60'
        }
      >
        {known ? count : '—'}
      </span>
      <span className="sr-only">
        {known
          ? t('nav.live.onSiteNow', '{{n}} on site now', { n: count })
          : t('nav.live.unknown', 'On site now: not available')}
      </span>
    </span>
  );
}
